import type { EChartsOption } from '../echarts-types.ts';
import type { ChartConfig } from '../types.ts';
import { bodyTooltip, chartTitle, type OptionContext } from './shared.ts';

export function buildWorldMapOption(
  config: Extract<ChartConfig, { type: 'world_map' }>,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor, dark } = theme;
  const labels = config.labels.map((c) => c.trim().toUpperCase());
  const vals = config.values;
  const data = labels.map((name, i) => ({
    name,
    value: vals[i] ?? 0,
  }));
  const tooltipByCode: Record<
    string,
    { code: string; label: string; pct: number; count: number }
  > = {};
  if (config.tooltips) {
    for (const tip of config.tooltips) {
      tooltipByCode[tip.code.trim().toUpperCase()] = tip;
    }
  }
  const valueKind =
    config.valueKind ?? (Math.max(...vals, 0) > 100 ? 'count' : 'pct');
  const vmin = Math.min(...vals, 0);
  const vmax = Math.max(...vals, vmin + 1e-6);
  const border = dark ? 'rgba(255,255,255,0.28)' : '#94a3b8';
  const noDataFill = dark ? '#3f3f46' : '#e2e8f0';
  const scaleColors = dark
    ? ['#52525b', '#a16207', '#fb923c']
    : ['#f1f5f9', '#fdba74', '#ea580c'];
  const hoverFill = dark ? '#fbbf24' : colors.primary;
  const showVisualMap = config.showVisualMap !== false;
  const mapLayout: Record<string, unknown> = {};
  if (config.layoutCenter) mapLayout['layoutCenter'] = config.layoutCenter;

  if (config.layoutSize !== undefined) {
    mapLayout['layoutSize'] = config.layoutSize;
  }
  if (config.aspectScale !== undefined) {
    mapLayout['aspectScale'] = config.aspectScale;
  }

  return {
    ...base,
    title: chartTitle(config.title, textColor, 'center'),
    tooltip: bodyTooltip({
      trigger: 'item',
      formatter: (p) => {
        const item = p as { name?: string; value?: number | string };
        const code = (item.name ?? '').trim().toUpperCase();
        if (code === '' || code === '-99') return '—';
        const tip = tooltipByCode[code];
        if (tip) {
          return `${tip.label}: ${String(tip.pct)}% of reviewers (${String(tip.count)} people)`;
        }
        const v =
          typeof item.value === 'number' ? item.value : Number(item.value);
        return `${code}: ${Number.isFinite(v) ? v : '—'}`;
      },
    }),
    // Continuous visualMap is drawn as a vertical bar in local coords, then rotated
    // for `orient: 'horizontal'`. So itemWidth = thickness, itemHeight = length.
    // Never enable `calculable`: handleSize defaults to 120% of itemWidth and with a
    // mistaken long-side itemWidth (~200) the handle renders as a giant orange pill
    // over the map (seen in Ask + Hub audience choropleths).
    visualMap: {
      show: showVisualMap,
      type: 'continuous',
      min: vmin,
      max: vmax,
      seriesIndex: 0,
      calculable: false,
      inRange: { color: scaleColors },
      outOfRange: { color: noDataFill },
      ...(showVisualMap
        ? {
            orient: 'horizontal' as const,
            left: 'center',
            bottom: 6,
            itemWidth: 12,
            itemHeight: 180,
            text:
              valueKind === 'count'
                ? ['More reviewers', 'Fewer']
                : ['Higher share', 'Lower share'],
            textStyle: {
              color: dark ? 'rgba(255,255,255,0.72)' : 'rgba(30,41,59,0.85)',
              fontSize: 10,
            },
          }
        : {}),
    },
    series: [
      {
        name: config.title || 'Regions',
        type: 'map',
        map: 'world',
        roam: config.roam ?? true,
        ...mapLayout,
        nameProperty: 'ISO_A2_EH',
        itemStyle: {
          areaColor: noDataFill,
          borderColor: border,
          borderWidth: 0.45,
        },
        emphasis: {
          label: {
            show: true,
            color: dark ? 'rgba(255,255,255,0.92)' : '#0f172a',
            fontSize: 10,
          },
          itemStyle: { areaColor: hoverFill, borderColor: border },
        },
        select: { disabled: true },
        data,
      },
    ],
  };
}
